import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardContent, Typography, Grid, Box, Divider,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Alert, Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { defaultCustomerConfig } from '../theme/theme';
import { exportPdf } from './PdfExport.js';

const fmtILS = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 2 }).format(n);
const fmtAmount = (n) =>
  new Intl.NumberFormat('he-IL', { maximumFractionDigits: 2 }).format(n);

const fmtNum = (n, decimals = 3) => Number(n).toFixed(decimals);

export default function ResultsTable({ result, formValues, config = defaultCustomerConfig }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';
  const captureRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    if (result) setTimeout(() => headingRef.current?.focus(), 0);
  }, [result]);

  if (!result) return null;

  const { specificResult, comparisonTable } = result;
  const { coefficient, monthlyPension, effectiveSecurityPeriod, orphansSummary = [] } = specificResult;
  const requestedPeriod = formValues ? Number(formValues.securityPeriod) : effectiveSecurityPeriod;
  const securityCapped = effectiveSecurityPeriod < requestedPeriod;

  const { securityPeriods = [], spouseRates = [], matrix = {} } = comparisonTable || {};

  const selectedRate = formValues ? Math.round(Number(formValues.spousePensionRate)) : null;
  const selectedPeriod = effectiveSecurityPeriod;

  const colLabel = (p) =>
    p === 0 ? t('securityPeriodCol0') : t('securityPeriodCol', { months: p });

  const handlePdf = () => {
    exportPdf({ captureRef, formValues, config });
  };

  const hasSpouseForm = formValues ? formValues.hasSpouse : true;
  const displayRates  = hasSpouseForm ? spouseRates : spouseRates.filter(r => r === 0);
  const renderComparisonTable = (title, getCellContent) => (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography component="h2" variant="h6" gutterBottom>{title}</Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" aria-label={title} role="list" sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            <TableHead role="presentation">
              <TableRow role="presentation" sx={{ backgroundColor: config.primaryColor }}>
                <TableCell role="presentation" sx={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {t('spousePensionRate')}
                </TableCell>
                {securityPeriods.map(p => (
                  <TableCell key={p} role="presentation" align="center" sx={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {colLabel(p)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody role="presentation">
              {displayRates.map((rate) => {
                const isSelectedRow = hasSpouseForm
                  ? (selectedRate !== null && Math.round(rate * 100) === selectedRate)
                  : rate === 0;
                const rateIsZero = rate === 0;
                return (
                  <TableRow
                    key={rate}
                    role="listitem"
                    sx={{
                      backgroundColor: isSelectedRow ? 'rgba(0,178,178,0.12)' : 'inherit',
                      '&:hover': { backgroundColor: `${config.primaryColor}0A` },
                    }}
                  >
                    <TableCell role="presentation" sx={{ fontWeight: isSelectedRow ? 700 : 400, whiteSpace: 'nowrap' }}>
                      {Math.round(rate * 100)}%
                    </TableCell>
                    {securityPeriods.map(p => {
                      const isHighlighted = isSelectedRow && p === selectedPeriod;
                      // 0% row cells are blank when hasSpouseForm=true
                      const cell = (hasSpouseForm && rateIsZero)
                        ? null
                        : matrix[String(rate)]?.[String(p)];
                      return (
                        <TableCell
                          key={p}
                          role="presentation"
                          align="center"
                          sx={{
                            fontWeight: isHighlighted ? 700 : 400,
                            color: isHighlighted ? '#005555' : 'inherit',
                            backgroundColor: isHighlighted ? `${config.ctaColor}30` : 'inherit',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getCellContent(cell)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  return (
    <Box ref={captureRef}>
      {/* Specific result card */}
      <Card sx={{ mb: 2, border: `2px solid ${config.ctaColor}` }} data-testid="results-card">
        <CardContent sx={{ p: 3 }}>
          <Typography ref={headingRef} tabIndex={-1} component="h2" variant="h6" gutterBottom sx={{ outline: 'none' }}>{t('resultTitle')}</Typography>
          <Divider sx={{ mb: 2 }} />
          {(formValues?.clientName || formValues?.clientId) && (
            <Box sx={{ mb: 1 }}>
              {formValues.clientName && (
                <Typography variant="body2">{t('clientName')}: {formValues.clientName}</Typography>
              )}
              {formValues.clientId && (
                <Typography variant="body2">{t('clientId')}: {formValues.clientId}</Typography>
              )}
              <Typography variant="body2" sx={{ color: '#5E6069' }}>
                {t('generatedAt')}: {new Date().toLocaleDateString('he-IL')}
              </Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#5E6069' }}>{t('coefficient')}</Typography>
              <Typography variant="body1" sx={{ color: config.primaryColor, fontWeight: 700, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                {fmtNum(coefficient)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: '#5E6069' }}>{t('monthlyPension')}</Typography>
              <Typography variant="body1" sx={{ color: config.ctaColor, fontWeight: 700, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                {fmtILS(monthlyPension)}
              </Typography>
            </Grid>
          </Grid>
          {hasSpouseForm && monthlyPension > 0 && formValues?.spousePensionRate > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#5E6069' }}>
                {t('spousePensionBreakdown', {
                  rate: Math.round(Number(formValues.spousePensionRate)),
                  amount: fmtILS(Math.round(monthlyPension * Number(formValues.spousePensionRate) / 100 * 100) / 100)
                })}
              </Typography>
            </Box>
          )}
          {securityCapped && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('securityNote', { requested: requestedPeriod, applied: effectiveSecurityPeriod })}
            </Alert>
          )}
          {orphansSummary.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#5E6069', fontWeight: 700, mb: 0.5 }}>
                {t('orphanSummaryTitle')}
              </Typography>
              {orphansSummary.map((orphan, i) => {
                const orphanPension = monthlyPension > 0
                  ? fmtILS(Math.round(monthlyPension * orphan.rate / 100 * 100) / 100)
                  : null;
                return (
                  <Typography key={i} variant="body2">
                    {t('orphanSummaryRow', { n: i + 1, rate: orphan.rate, date: orphan.expiryDate })}
                    {orphanPension && ` — ${orphanPension}`}
                  </Typography>
                );
              })}
            </Box>
          )}
          {specificResult.capitalization?.capCoefficient > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#5E6069', fontWeight: 700, mb: 1 }}>
                {t('capitalizationBreakdown')}
              </Typography>
              <Box component="ul" role="list" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {[
                  { label: 'מקדם היוון', value: Number(specificResult.capitalization.capCoefficient).toFixed(3) },
                  { label: 'סכום היוון', value: fmtILS(specificResult.capitalization.lumpSum) },
                  { label: 'קצבה בתקופת היוון', value: fmtILS(specificResult.capitalization.pensionDuringCap) },
                  { label: 'קצבה לאחר תקופת היוון', value: fmtILS(monthlyPension) },
                ].map(({ label, value }) => (
                  <Box component="li" key={label} role="listitem" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4, px: 1, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 0 } }}>
                    <Typography variant="body2" sx={{ color: '#5E6069', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap', direction: 'ltr' }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Comparison tables — pension amounts and coefficients */}
      {comparisonTable && displayRates.length > 0 && (
        <>
          {renderComparisonTable(t('comparisonTitle'), (cell) => cell != null ? fmtAmount(cell.pension) : '')}
          {renderComparisonTable(t('comparisonTitleCoeff'), (cell) => cell != null ? fmtNum(cell.coefficient) : '')}
          <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', mb: 2 }}>
            <Button
              data-html2canvas-ignore
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handlePdf}
              sx={{ fontWeight: 700 }}
            >
              {t('downloadPdf')}
            </Button>
          </Box>
        </>
      )}

    </Box>
  );
}
